import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  BarChart as RevenueIcon,
  PendingActions as OutstandingIcon,
  School as TrainingIcon,
  Assessment as ReportIcon,
  FlightTakeoff as ProjectRosterIcon,
  Assignment as ParticipantRosterIcon,
  Groups as BoardMembersIcon,
  Mail as MailingListIcon,
  HealthAndSafety as InsuranceIcon,
  VolunteerActivism as DRVolunteersIcon,
  HowToReg as CheckedInIcon,
  CardTravel as VisaIcon,
} from '@mui/icons-material';
import useAxiosPrivate from '../../../Hooks/useAxiosPrivate';
import styles from './Reporting.module.css';

// Map known ReportType keys to a meaningful icon. Unknown types fall back to ReportIcon.
const REPORT_ICONS = {
  'project-roster': ProjectRosterIcon,
  'participant-roster': ParticipantRosterIcon,
  'board-members': BoardMembersIcon,
  'mailing-list': MailingListIcon,
  'insurance': InsuranceIcon,
  'dr-volunteers': DRVolunteersIcon,
  'checked-in': CheckedInIcon,
  'visa': VisaIcon,
};

const Reporting = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReports = async () => {
    setIsLoading(true);
    setError(false);
    try {
      const response = await axiosPrivate.get('/api/system-reports');
      setReports(response.data || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError(true);
      enqueueSnackbar('Failed to load reports', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Combine the two featured reports with the dynamic ones from the API,
  // normalized so each card renders the same way.
  const allReports = useMemo(() => {
    const featured = [
      {
        id: 'featured-training',
        name: 'Training Report',
        description: 'Excel export with staff, trainees & one tab per class type',
        to: '/admin/reporting/training',
        Icon: TrainingIcon,
      },
      {
        id: 'featured-revenue',
        name: 'Revenue Report',
        description: 'Payments & refunds by date range, method, and account code',
        to: '/admin/reporting/revenue',
        Icon: RevenueIcon,
      },
      {
        id: 'featured-outstanding',
        name: 'Outstanding Payments Report',
        description: 'Orders with an unpaid balance, by date range, account code, and event',
        to: '/admin/reporting/outstanding',
        Icon: OutstandingIcon,
      },
    ];
    const dynamic = (reports || []).map((r) => ({
      id: `report-${r.ReportID}`,
      name: r.ReportName,
      description: r.ReportDescription || '',
      to: `/admin/reporting/run/${r.ReportType}`,
      Icon: REPORT_ICONS[r.ReportType] || ReportIcon,
    }));
    return [...featured, ...dynamic];
  }, [reports]);

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Reports</h1>
                <p>Run and export reports to quickly review key data and insights across the system.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className={styles.loadingContainer}>
              <CircularProgress />
              <span className={styles.loadingText}>Loading reports...</span>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Reports</h1>
                <p>Run and export reports to quickly review key data and insights across the system.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12">
                <div className="alert alert-danger" role="alert">
                  Error loading reports. Please try again later.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Blue Header Banner - text only, button moved to content area */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Reports</h1>
              <p>Run and export reports to quickly review key data and insights across the system.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        <Box className={styles.container}>

          {/* Add Report Button — top-left, matching Users page pattern */}
          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              startIcon={<Add />}
              className={styles.addButton}
              onClick={() => navigate('/admin/reporting/add')}
            >
              Add Report
            </Button>
          </Box>

          {/* All reports rendered as uniform icon cards */}
          {allReports.length === 0 ? (
            <div className="alert alert-info" role="alert">
              No reports available. <Link to="/admin/reporting/add">Add your first report</Link>.
            </div>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              {allReports.map(({ id, name, description, to, Icon }) => (
                <Paper
                  key={id}
                  component={Link}
                  to={to}
                  elevation={0}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    p: '14px 20px', border: '1px solid #e0e0e0', borderRadius: 2,
                    textDecoration: 'none', color: 'inherit', cursor: 'pointer',
                    transition: 'box-shadow 0.15s, border-color 0.15s',
                    '&:hover': { boxShadow: '0 2px 10px rgba(0,48,99,0.12)', borderColor: '#003063' },
                  }}
                >
                  <Box sx={{ bgcolor: '#e8f0fe', borderRadius: 1.5, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon sx={{ color: '#003063', fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#003063' }}>{name}</Typography>
                    {description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {description}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

        </Box>
      </div>
    </main>
  );
};

export default Reporting;
